/**
 * ActionPanel — fixed 4-slot layout matching wireframe 04.
 *
 * Slots: [Check/Call] [Bet/Raise] [Fold] [All In]
 *   - The 1st slot is "Check" when no bet to face, "Call $X" when facing one.
 *   - The 2nd slot is "Bet" when no bet to face, "Raise" when facing one.
 *     Tapping it expands the panel inline to show a size input (per
 *     wireframe footer note: "Bet button expands inline to reveal size input
 *     on tap.").
 *   - Slots that aren't legal in the current state are visually disabled.
 *
 * Per PRD V1 §9.1: Live mode only in V1 (no interactive post-hand mode).
 * Bet sizing uses freeform typed input with snap-to-5 per PRD §8.2.
 */

import { useState } from 'react';
import type { PlayerAction } from '@/types/action';
import type { HandState } from '@/types/handState';
import { legalActions, type ActionOption } from '@/lib/engine/actionValidator';
import { cn } from '@/lib/utils';

interface ActionPanelProps {
  state: HandState;
  onAction: (action: PlayerAction) => void;
  disabled?: boolean;
}

export function ActionPanel({ state, onAction, disabled }: ActionPanelProps) {
  const options = legalActions(state);
  const byKind = new Map(options.map((o) => [o.kind, o]));

  const facingBet = state.toCall > 0;

  // Slot 1: Check or Call
  const slot1Kind = facingBet ? 'call' : 'check';
  const slot1Option = byKind.get(slot1Kind);
  const slot1Label =
    slot1Kind === 'call' && slot1Option?.callAmount != null
      ? `Call $${slot1Option.callAmount}`
      : 'Check';

  // Slot 2: Bet or Raise (with inline size input)
  const slot2Kind = facingBet ? 'raise' : 'bet';
  const slot2Option = byKind.get(slot2Kind);

  // Slot 3: Fold
  const slot3Option = byKind.get('fold');

  // Slot 4: All-In
  const slot4Option = byKind.get('allin');

  const [sizing, setSizing] = useState(false);

  if (sizing && slot2Option) {
    return (
      <div className="flex min-h-[5rem] flex-col justify-center border-t border-border bg-surface-elevated px-4 py-3">
        <BetRaiseInput
          option={slot2Option}
          onConfirm={(total) => {
            const action: PlayerAction =
              slot2Kind === 'bet'
                ? { kind: 'bet', total }
                : { kind: 'raise', total };
            onAction(action);
            setSizing(false);
          }}
          onCancel={() => setSizing(false)}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-[5rem] flex-col justify-center border-t border-border bg-surface-elevated px-3 py-3">
      <div className="mx-auto grid w-full max-w-2xl grid-cols-4 gap-2">
        <SlotButton
          label={slot1Label}
          option={slot1Option}
          variant="primary"
          disabled={disabled}
          onClick={() => {
            if (!slot1Option) return;
            if (slot1Kind === 'call') onAction({ kind: 'call' });
            else onAction({ kind: 'check' });
          }}
        />
        <SlotButton
          label={facingBet ? 'Raise' : 'Bet'}
          option={slot2Option}
          variant="raise"
          disabled={disabled}
          onClick={() => {
            if (slot2Option) setSizing(true);
          }}
        />
        <SlotButton
          label="Fold"
          option={slot3Option}
          variant="fold"
          disabled={disabled}
          onClick={() => slot3Option && onAction({ kind: 'fold' })}
        />
        <SlotButton
          label="All In"
          option={slot4Option}
          variant="allin"
          disabled={disabled}
          onClick={() => slot4Option && onAction({ kind: 'allin' })}
        />
      </div>
    </div>
  );
}

// =============================================================================
// Slot Button
// =============================================================================

type SlotVariant = 'primary' | 'raise' | 'fold' | 'allin';

function SlotButton({
  label,
  option,
  variant,
  disabled,
  onClick,
}: {
  label: string;
  option: ActionOption | undefined;
  variant: SlotVariant;
  disabled?: boolean;
  onClick: () => void;
}) {
  const legal = option !== undefined;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || !legal}
      className={cn(
        'rounded-md border-2 px-3 py-2.5 text-sm font-semibold transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-border-focus focus:ring-offset-1',
        'disabled:cursor-not-allowed disabled:opacity-30',
        variant === 'primary' &&
          'border-player-hero-accent bg-player-hero-accent/15 text-text-primary hover:bg-player-hero-accent/25',
        variant === 'raise' &&
          'border-brand-primary bg-brand-primary text-text-on-brand hover:bg-brand-primary-hover',
        variant === 'fold' &&
          'border-border bg-surface-elevated text-text-secondary hover:border-border-strong hover:text-text-primary',
        variant === 'allin' &&
          'border-action-allin bg-action-allin/15 text-action-allin hover:bg-action-allin/25',
      )}
    >
      {label}
    </button>
  );
}

// =============================================================================
// Bet/Raise input
// =============================================================================

function BetRaiseInput({
  option,
  onConfirm,
  onCancel,
}: {
  option: ActionOption;
  onConfirm: (total: number) => void;
  onCancel: () => void;
}) {
  const min = option.minTotal ?? 0;
  const max = option.maxTotal ?? 0;
  const [value, setValue] = useState<string>(min.toString());

  const numeric = Number(value);
  const valid = Number.isFinite(numeric) && numeric >= min && numeric <= max;

  return (
    <div className="mx-auto flex max-w-2xl flex-wrap items-center justify-center gap-2">
      <span className="text-sm text-text-secondary">
        {option.kind === 'bet' ? 'Bet to' : 'Raise to'}
      </span>
      <input
        type="number"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        min={min}
        max={max}
        step={5}
        autoFocus
        className={cn(
          'w-28 rounded-md border bg-surface-app px-2 py-1.5 text-sm',
          'text-text-primary placeholder:text-text-muted',
          'focus:outline-none focus:ring-2 focus:ring-border-focus',
          valid ? 'border-border' : 'border-state-error',
        )}
      />
      <span className="text-xs text-text-muted">
        min ${min} · max ${max}
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-border bg-surface-elevated px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => valid && onConfirm(numeric)}
          disabled={!valid}
          className={cn(
            'rounded-md bg-brand-primary px-4 py-1.5 text-sm font-semibold',
            'text-text-on-brand hover:bg-brand-primary-hover',
            'focus:outline-none focus:ring-2 focus:ring-border-focus focus:ring-offset-1',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          Confirm
        </button>
      </div>
    </div>
  );
}
